/**
 * POS Customer Selection Sheet
 * Extracted from POSPage.jsx for maintainability.
 */
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription
} from "@/components/ui/sheet";
import { User, Search, Loader2, UserPlus, Star } from "lucide-react";
import { cn } from "@/lib/utils";

export function POSCustomerSheet({
  open,
  onOpenChange,
  customerSearch,
  setCustomerSearch,
  fetchCustomers,
  customersLoading,
  customersList,
  selectedCustomer,
  onSelectCustomer,
  onClearCustomer,
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Select Customer
          </SheetTitle>
          <SheetDescription>
            Search and select a customer for this order, or continue as walk-in.
          </SheetDescription>
        </SheetHeader>
        
        <div className="mt-4 space-y-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or phone..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchCustomers(customerSearch)}
                className="pl-9"
                data-testid="customer-search-input"
              />
            </div>
            <Button onClick={() => fetchCustomers(customerSearch)} disabled={customersLoading}>
              {customersLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" size="sm" className="flex-1"
              onClick={() => { onClearCustomer(); onOpenChange(false); }}
            >
              Walk-in Customer
            </Button>
            <Button 
              variant="outline" size="sm" className="flex-1 gap-1"
              onClick={() => window.open('/admin/customers', '_blank')}
            >
              <UserPlus className="h-4 w-4" />
              New Customer
            </Button>
          </div>
          
          <Separator />
          
          <ScrollArea className="h-[calc(100vh-280px)]">
            <div className="space-y-2 pr-4">
              {customersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : customersList.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <User className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No customers found</p>
                  <p className="text-xs">Try a different search term</p>
                </div>
              ) : (
                customersList.map((customer) => (
                  <Card 
                    key={customer.id} 
                    className={cn(
                      "p-3 cursor-pointer hover:bg-accent transition-colors",
                      selectedCustomer?.id === customer.id && "border-primary bg-primary/5"
                    )}
                    onClick={() => onSelectCustomer(customer)}
                    data-testid={`customer-item-${customer.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{customer.name}</p>
                          <p className="text-sm text-muted-foreground">{customer.phone}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-amber-600">
                          <Star className="h-4 w-4 fill-current" />
                          <span className="font-medium">{customer.loyalty_points || 0}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">points</p>
                      </div>
                    </div>
                    {customer.email && (
                      <p className="text-xs text-muted-foreground mt-1 truncate">{customer.email}</p>
                    )}
                  </Card>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
}
